import type { Tracer } from '@zg-rentals/trace-base';

const TRACE_COOKIE_NAME = 'rjs-trace';
const TRACE_COOKIE_DURATION_SEC = 31536000;
const SESSION_ID_LENGTH = 26;

export class BrowserTracer implements Tracer {
  private sessionId = this.makeID();

  public init() {
    const [priorSessionId] = this.getTraceCookie().split(':');
    if (priorSessionId) {
      this.sessionId = priorSessionId;
    }

    this.tracePageViews();
    this.traceAjax();

    return this;
  }

  private tracePageViews() {
    // Clear any existing trace ID,
    // because it represents the page load that just occurred.
    this.setTraceCookie('');
    // Before this page is unloaded, set a new trace ID -
    // this will be the trace ID for the subsequent page load.
    window.addEventListener('beforeunload', () => {
      this.setTraceCookie(this.makeID());
    });
  }

  public makeTraceID() {
    let currentTimeMs: number;
    if (typeof window !== 'undefined' && window.performance?.timeOrigin) {
      // Includes microsecond digits
      currentTimeMs = window.performance.now() + window.performance.timeOrigin;
    } else {
      currentTimeMs = new Date().getTime();
    }
    const currentTimeNs = currentTimeMs * 1_000_000;
    const formattedTimestamp = currentTimeNs.toString(16).padStart(16, '0').substring(0, 16);
    return formattedTimestamp + this.makeID(16);
  }

  private traceAjax() {
    this.onAjaxRequest(() => this.setTraceCookie(this.makeTraceID()));
    this.onAjaxResponse(() => this.setTraceCookie(''));
  }

  private onAjaxRequest(callback: () => void) {
    const fetch = window.fetch;
    window.fetch = (...args): Promise<Response> => {
      callback();
      return fetch(...args);
    };
    const send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function (...args) {
      callback();
      return send.call(this, ...args);
    };
  }

  private onAjaxResponse(callback: () => void) {
    const fetch = window.fetch;
    window.fetch = (...args): Promise<Response> => {
      return fetch(...args).finally(callback);
    };
    const send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function (...args) {
      this.addEventListener('load', callback);
      this.addEventListener('error', callback);
      return send.call(this, ...args);
    };
  }

  private getTraceCookie() {
    const traceCookiePrefix = `${TRACE_COOKIE_NAME}=`;
    const cookieRows = document.cookie.split('; ');
    const traceCookie = cookieRows.find((row) => row.startsWith(traceCookiePrefix)) || '';
    return traceCookie.replace(traceCookiePrefix, '');
  }

  private setTraceCookie(traceId = '') {
    const saveTraceFlag = window.location.search.match(/[?&]debug([^\w]|$)/i) ? '1' : '';
    document.cookie = `${TRACE_COOKIE_NAME}=${this.sessionId}:${traceId}:${saveTraceFlag}; path=/; max-age=${TRACE_COOKIE_DURATION_SEC}; samesite=strict`;
  }

  private makeID(length = SESSION_ID_LENGTH) {
    let id = '';
    while (id.length < length) {
      id += Math.random().toString(16).slice(2);
    }
    return id.slice(0, length);
  }
}
